import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Share2, Copy, Mail, MessageCircle, Check, Clock, Percent, Gift, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Discount structure display
const DiscountTable = ({ currentPosition }) => {
  const discounts = [];
  for (let i = 1; i <= 8; i++) {
    const discount = i === 1 ? 15 : Math.max(15 - ((i - 1) * 2), 5);
    discounts.push({ position: i, discount });
  }
  
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <h4 className="font-semibold text-gray-700 mb-3">Rabatt-Staffelung</h4>
      <div className="grid grid-cols-4 gap-2 text-sm">
        {discounts.map(({ position, discount }) => (
          <div 
            key={position}
            className={`p-2 rounded text-center ${
              position < currentPosition 
                ? 'bg-gray-200 text-gray-500' 
                : position === currentPosition 
                  ? 'bg-green-500 text-white font-bold' 
                  : 'bg-white border border-gray-200'
            }`}
          >
            <div className="text-xs text-gray-500">{position}.</div>
            <div className="font-semibold">{discount}%</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-2 text-center">
        Je früher du bestellst, desto mehr sparst du!
      </p>
    </div>
  );
};

// Create Group Form
const CreateGroupForm = ({ onGroupCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    creator_name: '',
    creator_email: '',
    product_type: 'abschlussarbeit'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/groups/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Fehler beim Erstellen');
      }
      
      const data = await res.json();
      onGroupCreated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Gruppenname *
        </label>
        <input
          type="text"
          required
          placeholder="z.B. Matura-Klasse 6a Kanti Zürich"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Dein Name *
          </label>
          <input
            type="text"
            required
            placeholder="Max Muster"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            value={formData.creator_name}
            onChange={(e) => setFormData({...formData, creator_name: e.target.value})}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Deine E-Mail *
          </label>
          <input
            type="email"
            required
            placeholder="max@example.ch"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            value={formData.creator_email}
            onChange={(e) => setFormData({...formData, creator_email: e.target.value})}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Was wollt ihr drucken?
        </label>
        <select
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          value={formData.product_type}
          onChange={(e) => setFormData({...formData, product_type: e.target.value})}
        >
          <option value="abschlussarbeit">Abschlussarbeit / Thesis</option>
          <option value="maturarbeit">Maturarbeit</option>
          <option value="skript">Skript / Unterlagen</option>
          <option value="flyer">Flyer / Karten</option>
          <option value="andere">Andere</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
        data-testid="create-group-btn"
      >
        {loading ? (
          <>Wird erstellt...</>
        ) : (
          <>
            <Users size={20} />
            Gruppe erstellen & 15% sichern
          </>
        )}
      </button>
    </form>
  );
};

// Share Group Component
const ShareGroup = ({ groupData }) => {
  const [copied, setCopied] = useState(false);
  
  const shareUrl = `https://adagmedia.ch/gruppe/${groupData.code}`;
  
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const whatsappText = encodeURIComponent(
    `Hey! Ich habe eine Sammelbestellung bei ADAG Print gestartet für "${groupData.name}". Bestelle auch und spar bis zu 15%! Je früher, desto mehr Rabatt: ${shareUrl}`
  );

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">Gruppe erstellt!</h2>
        <p className="text-gray-600 mt-1">Teile den Link und sammle Rabatte</p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
        <div className="text-sm text-green-700">Dein Rabatt als Gründer</div>
        <div className="text-4xl font-bold text-green-600">15%</div>
        <div className="text-sm text-green-700 mt-1">
          + CHF 10 Bonus wenn 2+ andere bestellen
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
        <div className="font-semibold text-yellow-800 flex items-center gap-2">
          <Gift size={18} />
          Dein Rabattcode
        </div>
        <div className="mt-2 bg-white border-2 border-dashed border-yellow-400 rounded-lg p-3 text-center">
          <span className="text-2xl font-mono font-bold text-gray-800">{groupData.coupon_code || 'GRUPPE15'}</span>
        </div>
        <p className="text-xs text-yellow-700 mt-2">
          Wird automatisch angewendet wenn du über den Link bestellst
        </p>
      </div>

      <a
        href={groupData.checkout_url || 'https://adagmedia.ch/abschlussarbeiten.html'}
        className="block w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-center"
        data-testid="order-now-btn"
      >
        Jetzt mit 15% Rabatt bestellen
        <ChevronRight className="inline ml-2" size={20} />
      </a>

      <div>
        <div className="text-sm font-medium text-gray-700 mb-2">Gruppen-Code</div>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={shareUrl}
            className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700"
          />
          <button
            onClick={copyLink}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            data-testid="copy-link-btn"
          >
            {copied ? <Check size={20} className="text-green-600" /> : <Copy size={20} />}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <a
          href={`https://wa.me/?text=${whatsappText}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold py-3 px-4 rounded-lg transition-colors"
          data-testid="share-whatsapp-btn"
        >
          <MessageCircle size={20} />
          WhatsApp
        </a>
        <a
          href={`mailto:?subject=Sammelbestellung: ${encodeURIComponent(groupData.name)}&body=${encodeURIComponent(`Ich habe eine Sammelbestellung gestartet. Bestelle auch und spar bis zu 15%!\n\n${shareUrl}`)}`}
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
          data-testid="share-email-btn"
        >
          <Mail size={20} />
          E-Mail
        </a>
      </div>

      <div className="border-t pt-4">
        <DiscountTable currentPosition={1} />
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Clock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-yellow-800">30 Tage gültig</div>
            <div className="text-sm text-yellow-700">
              Deine Gruppe läuft bis {new Date(groupData.expires_at).toLocaleDateString('de-CH')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Join Group Component
const JoinGroup = ({ code }) => {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joinForm, setJoinForm] = useState({ member_name: '', member_email: '' });
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(null);

  useEffect(() => {
    fetchGroup();
  }, [code]);

  const fetchGroup = async () => {
    try {
      const res = await fetch(`${API_URL}/api/groups/${code}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Gruppe nicht gefunden');
      }
      const data = await res.json();
      setGroup(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setJoining(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/groups/${code}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(joinForm)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Fehler beim Beitreten');
      }
      
      const data = await res.json();
      setJoined(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
        <div className="text-red-600 text-lg font-semibold">{error}</div>
        <p className="text-gray-600 mt-2">
          Möglicherweise ist die Gruppe abgelaufen oder der Link ist falsch.
        </p>
      </div>
    );
  }

  if (joined) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">Du bist dabei!</h2>
          <p className="text-gray-600 mt-1">{joined.group_name}</p>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <div className="text-sm text-green-700">Dein Rabatt (Position {joined.your_position})</div>
          <div className="text-5xl font-bold text-green-600">{joined.your_discount}</div>
        </div>

        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
          <div className="font-semibold text-yellow-800 flex items-center gap-2">
            <Gift size={18} />
            Dein Rabattcode
          </div>
          <div className="mt-2 bg-white border-2 border-dashed border-yellow-400 rounded-lg p-3 text-center">
            <span className="text-2xl font-mono font-bold text-gray-800">{joined.coupon_code}</span>
          </div>
          <p className="text-xs text-yellow-700 mt-2">
            Der Code wird automatisch angewendet wenn du auf "Jetzt bestellen" klickst
          </p>
        </div>

        <a
          href={joined.checkout_url || `https://adagmedia.ch/abschlussarbeiten.html`}
          className="block w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors text-center text-lg"
          data-testid="order-with-discount-btn"
        >
          Jetzt bestellen mit {joined.your_discount} Rabatt
          <ChevronRight className="inline ml-2" size={20} />
        </a>

        <p className="text-sm text-gray-500 text-center">
          Die nächste Person erhält {joined.next_person_discount} Rabatt
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <Users className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800">{group.name}</h2>
        <p className="text-gray-600 mt-1">
          Gestartet von {group.creator_name} · {group.total_members} Mitglied(er)
        </p>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
        <div className="text-sm text-green-700">Dein Rabatt wenn du JETZT beitrittst</div>
        <div className="text-4xl font-bold text-green-600">{group.your_discount_if_join_now}</div>
        <div className="text-sm text-orange-600 mt-1 font-medium">
          Je später du beitrittst, desto weniger Rabatt!
        </div>
      </div>

      <DiscountTable currentPosition={group.total_members + 1} />

      <form onSubmit={handleJoin} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Dein Name *
          </label>
          <input
            type="text"
            required
            placeholder="Max Muster"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            value={joinForm.member_name}
            onChange={(e) => setJoinForm({...joinForm, member_name: e.target.value})}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Deine E-Mail *
          </label>
          <input
            type="email"
            required
            placeholder="max@example.ch"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            value={joinForm.member_email}
            onChange={(e) => setJoinForm({...joinForm, member_email: e.target.value})}
          />
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={joining}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
          data-testid="join-group-btn"
        >
          {joining ? (
            <>Wird verarbeitet...</>
          ) : (
            <>
              <Percent size={20} />
              Jetzt beitreten & {group.your_discount_if_join_now} sichern
            </>
          )}
        </button>
      </form>

      <div className="flex items-center gap-2 text-sm text-gray-500 justify-center">
        <Clock size={16} />
        Noch {group.days_remaining} Tage gültig
      </div>
    </div>
  );
};

// Main Page Component
const Zusammenbesteller = () => {
  const { code } = useParams();
  const [createdGroup, setCreatedGroup] = useState(null);

  // If we have a code in URL, show join view
  if (code) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
        <div className="max-w-md mx-auto">
          <JoinGroup code={code} />
        </div>
      </div>
    );
  }

  // If group was just created, show share view
  if (createdGroup) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
        <div className="max-w-md mx-auto">
          <ShareGroup groupData={createdGroup} />
        </div>
      </div>
    );
  }

  // Default: show create group form
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <Users className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800">Zusammenbesteller</h1>
          <p className="text-gray-600 mt-2 text-lg">
            Starte eine Sammelbestellung und spar bis zu 15%
          </p>
        </div>

        {/* How it works */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4">So funktioniert's:</h3>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">1</div>
              <div>
                <div className="font-medium">Gruppe erstellen</div>
                <div className="text-sm text-gray-600">Du startest mit 15% Rabatt als Gründer</div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">2</div>
              <div>
                <div className="font-medium">Link teilen</div>
                <div className="text-sm text-gray-600">Per WhatsApp oder E-Mail an Klassenkameraden</div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">3</div>
              <div>
                <div className="font-medium">Alle bestellen</div>
                <div className="text-sm text-gray-600">Je früher, desto mehr Rabatt (15% → 13% → 11% ...)</div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 font-bold">
                <Gift size={16} />
              </div>
              <div>
                <div className="font-medium">Bonus für Gründer</div>
                <div className="text-sm text-gray-600">CHF 10 extra wenn 2+ andere bestellen</div>
              </div>
            </div>
          </div>
        </div>

        {/* Create Form */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Gruppe erstellen</h3>
          <CreateGroupForm onGroupCreated={setCreatedGroup} />
        </div>

        {/* Trust badges */}
        <div className="mt-8 flex items-center justify-center gap-6 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <Clock size={16} />
            30 Tage gültig
          </div>
          <div className="flex items-center gap-2">
            <Check size={16} />
            Sofort-Rabatt
          </div>
        </div>
      </div>
    </div>
  );
};

export default Zusammenbesteller;
